import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { t } = useTranslation('common');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Added error handling to make sign‑out more robust
  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      // Log the error; in a real app you might show a toast/notification
      console.error('Sign out failed:', error);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" onClick={closeMobileMenu}>
            <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg relative overflow-hidden">
              {/* Modern Pickleball Design */}
              <div className="relative">
                {/* Paddle outline - sleeker design */}
                <div className="w-5 h-6 relative">
                  {/* Main paddle shape */}
                  <div className="w-4 h-4 bg-white rounded-full relative top-0 left-0.5">
                    {/* Hole pattern - more geometric */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="grid grid-cols-2 gap-0.5">
                        <div className="w-0.5 h-0.5 bg-green-500 rounded-full"></div>
                        <div className="w-0.5 h-0.5 bg-green-500 rounded-full"></div>
                        <div className="w-0.5 h-0.5 bg-green-500 rounded-full"></div>
                        <div className="w-0.5 h-0.5 bg-green-500 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  {/* Handle - more refined */}
                  <div className="w-1.5 h-2 bg-white rounded-b-sm absolute bottom-0 left-1/2 transform -translate-x-1/2"></div>
                </div>
              </div>
              
              {/* Subtle highlight effect */}
              <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-white/20 to-transparent rounded-xl"></div>
            </div>
            <span className="text-xl font-bold text-gray-900">HKPicklePlay</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive('/') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              {t('home')}
            </Link>
            <Link
              to="/courts"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive('/courts') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              {t('courts')}
            </Link>
            <Link
              to="/sessions"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive('/sessions') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              {t('sessions')}
            </Link>
            <Link
              to="/learning"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive('/learning') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              {t('learning')}
            </Link>
            <Link
              to="/scoreboard"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive('/scoreboard') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              {t('scoreboard')}
            </Link>
          </nav>

          {/* Desktop Right side */}
          <div className="hidden md:flex items-center space-x-4">
            <LanguageSwitcher />

            {user ? (
              <div className="flex items-center space-x-4">
                <Link
                  to="/profile"
                  className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <i className="ri-user-line text-gray-600"></i>
                  </div>
                  <span className="text-sm font-medium">{user.email}</span>
                </Link>
                <button
                  onClick={handleSignOut}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('sign_out', 'Sign Out')}
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('login')}
                </Link>
                <Link
                  to="/signup"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('signup')}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button and language switcher */}
          <div className="md:hidden flex items-center space-x-2">
            <LanguageSwitcher />
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                to="/"
                onClick={closeMobileMenu}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors cursor-pointer ${
                  isActive('/') 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
              >
                <i className="ri-home-line mr-3"></i>
                {t('home')}
              </Link>
              <Link
                to="/courts"
                onClick={closeMobileMenu}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors cursor-pointer ${
                  isActive('/courts') 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
              >
                <i className="ri-map-pin-line mr-3"></i>
                {t('courts')}
              </Link>
              <Link
                to="/sessions"
                onClick={closeMobileMenu}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors cursor-pointer ${
                  isActive('/sessions') 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
              >
                <i className="ri-calendar-line mr-3"></i>
                {t('sessions')}
              </Link>
              <Link
                to="/learning"
                onClick={closeMobileMenu}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors cursor-pointer ${
                  isActive('/learning') 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
              >
                <i className="ri-book-line mr-3"></i>
                {t('learning')}
              </Link>
              <Link
                to="/scoreboard"
                onClick={closeMobileMenu}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors cursor-pointer ${
                  isActive('/scoreboard') 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
              >
                <i className="ri-trophy-line mr-3"></i>
                {t('scoreboard')}
              </Link>

              {/* Mobile User Section */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                {user ? (
                  <>
                    <Link
                      to="/profile"
                      onClick={closeMobileMenu}
                      className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                        <i className="ri-user-line text-gray-600"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium">{t('profile', 'Profile')}</div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                      </div>
                    </Link>
                    <button
                      onClick={() => {
                        handleSignOut();
                        closeMobileMenu();
                      }}
                      className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-red-600 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-logout-box-line mr-3"></i>
                      {t('sign_out', 'Sign Out')}
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={closeMobileMenu}
                      className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-login-box-line mr-3"></i>
                      {t('login')}
                    </Link>
                    <Link
                      to="/signup"
                      onClick={closeMobileMenu}
                      className="block px-3 py-2 rounded-md text-base font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors cursor-pointer mt-2"
                    >
                      <i className="ri-user-add-line mr-3"></i>
                      {t('signup')}
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
